import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Sparkles, AlertTriangle, ChevronDown, ChevronUp, Copy, Check, FileWarning, ShieldAlert, Send, Gavel, Download } from "lucide-react";
import AnalysisExtras from "./AnalysisExtras";

const PRIORITY_STYLES = {
  high: "bg-red-500/15 text-red-300 border-red-500/30",
  medium: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  low: "bg-white/10 text-white/60 border-white/15",
};

const ACTION_LABELS = {
  dispute: "Dispute",
  validation: "Debt validation",
  goodwill: "Goodwill letter",
  pay_for_delete: "Pay-for-delete",
  wait: "Wait it out",
};

function NegativeItem({ item, document, hasAddress }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [dispute, setDispute] = useState(null); // populated after generate

  const copyLetter = async () => {
    try {
      await navigator.clipboard.writeText(item.dispute_letter || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.warn("Clipboard write failed:", e);
    }
  };

  const generateDispute = async () => {
    if (!document?.id) return;
    setGenerating(true);
    try {
      const { data } = await api.post("/disputes", {
        document_id: document.id,
        item_id: item.id,
      });
      setDispute(data.dispute);
      // notify DisputesPanel to refresh
      window.dispatchEvent(new CustomEvent("dispute-created", { detail: data.dispute }));
      // immediately fetch a signed PDF URL and open it
      try {
        const { data: pdf } = await api.get(`/disputes/${data.dispute.id}/pdf`);
        window.open(pdf.url, "_blank", "noopener,noreferrer");
      } catch (e) {
        console.warn("PDF fetch after dispute create failed:", e);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't generate the dispute letter.";
      alert(msg);
    } finally {
      setGenerating(false);
    }
  };

  const priClass = PRIORITY_STYLES[item.priority] || PRIORITY_STYLES.low;

  return (
    <div className="border border-white/10 rounded-lg overflow-hidden" data-testid={`neg-item-${item.id}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-start justify-between gap-3 p-3 text-left hover:bg-white/5 transition-all"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${priClass}`}>
              {item.priority || "low"}
            </span>
            <span className="text-[10px] uppercase tracking-wider text-white/45" style={{ fontFamily: "Inter, sans-serif" }}>
              {item.bureau || "—"} · {item.type?.replace(/_/g, " ") || "item"}
            </span>
          </div>
          <div className="font-display text-base text-white truncate">
            {item.creditor || "Unknown creditor"}
            {item.account_last4 ? <span className="text-white/40 text-sm"> · ••{item.account_last4}</span> : null}
          </div>
          <div className="text-xs text-white/55 mt-0.5" style={{ fontFamily: "Inter, sans-serif" }}>
            {item.status}{item.amount_usd != null ? ` · $${item.amount_usd.toLocaleString()}` : ""}{item.date_reported ? ` · ${item.date_reported}` : ""}
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-white/50 shrink-0 mt-1" /> : <ChevronDown className="w-4 h-4 text-white/50 shrink-0 mt-1" />}
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-white/5 space-y-3" data-testid={`neg-item-detail-${item.id}`}>
          <div>
            <div className="font-mono-label text-white/45 mb-1">Why it's hurting you</div>
            <p className="text-sm text-white/75" style={{ fontFamily: "Inter, sans-serif" }}>{item.why_negative}</p>
          </div>
          <div>
            <div className="font-mono-label text-white/45 mb-1">FCRA dispute angle</div>
            <p className="text-sm text-[var(--if-gold)]" style={{ fontFamily: "Inter, sans-serif" }}>{item.dispute_angle}</p>
          </div>
          <div>
            <div className="font-mono-label text-white/45 mb-1">Recommended action</div>
            <span className="inline-block text-xs px-2.5 py-1 rounded-full bg-[var(--if-gold)]/15 text-[var(--if-gold)]">
              {ACTION_LABELS[item.recommended_action] || item.recommended_action}
            </span>
          </div>
          {item.dispute_letter && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <div className="font-mono-label text-white/45">Drafted dispute letter</div>
                <button onClick={copyLetter} className="btn-outline !py-1.5 !px-3 !text-[10px]" data-testid={`copy-letter-${item.id}`}>
                  {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
              <pre className="text-xs text-white/70 whitespace-pre-wrap font-sans p-3 rounded-lg bg-black/30 border border-white/10 leading-relaxed" style={{ fontFamily: "Inter, sans-serif" }}>
                {item.dispute_letter}
              </pre>

              <div className="mt-3 grid sm:grid-cols-2 gap-2">
                {/* DIY: Generate PDF for client to mail themselves */}
                <div className="p-3 rounded-lg bg-[var(--if-gold)]/[0.06] border border-[var(--if-gold)]/30">
                  <div className="font-mono-label text-[var(--if-gold)] mb-2">Self-mail (free)</div>
                  {dispute ? (
                    <div className="flex items-center gap-2 text-xs text-green-300" data-testid={`dispute-generated-${item.id}`} style={{ fontFamily: "Inter, sans-serif" }}>
                      <Check className="w-3.5 h-3.5" />
                      PDF saved to <strong>My Disputes</strong>.
                    </div>
                  ) : !hasAddress ? (
                    <div className="text-xs" style={{ fontFamily: "Inter, sans-serif" }}>
                      <Link to="/profile" className="text-[var(--if-gold)] underline" data-testid={`go-profile-${item.id}`}>
                        Add address →
                      </Link>
                    </div>
                  ) : (
                    <button
                      onClick={generateDispute}
                      disabled={generating}
                      className="btn-outline !py-2 !px-3 !text-[11px] disabled:opacity-60"
                      data-testid={`generate-dispute-${item.id}`}
                    >
                      <Gavel className="w-3.5 h-3.5" />
                      {generating ? "Generating…" : "Generate PDF"}
                    </button>
                  )}
                  <p className="text-[10px] text-white/40 mt-2 leading-relaxed" style={{ fontFamily: "Inter, sans-serif" }}>
                    Print, sign, and mail to one bureau yourself.
                  </p>
                </div>

                {/* Premium: We mail it for you */}
                <div className="p-3 rounded-lg bg-gradient-to-br from-[var(--if-gold)]/15 to-[var(--if-gold)]/5 border border-[var(--if-gold)]/40 relative overflow-hidden">
                  <div className="absolute top-2 right-2 text-[9px] font-semibold uppercase tracking-wider text-[var(--if-gold)] bg-black/40 px-2 py-0.5 rounded-full">
                    $69 · all 3 bureaus
                  </div>
                  <div className="font-mono-label text-[var(--if-gold)] mb-2">Premium Mail</div>
                  {!hasAddress ? (
                    <Link to="/profile" className="text-xs text-[var(--if-gold)] underline" style={{ fontFamily: "Inter, sans-serif" }}>
                      Add address →
                    </Link>
                  ) : (
                    <button
                      onClick={async () => {
                        try {
                          const { data } = await api.post("/disputes/premium-mail/checkout", {
                            document_id: document.id,
                            item_id: item.id,
                            origin_url: window.location.origin,
                          });
                          window.location.href = data.url;
                        } catch (err) {
                          alert(err?.response?.data?.detail || "Couldn't start checkout.");
                        }
                      }}
                      className="btn-gold !py-2 !px-3 !text-[11px]"
                      data-testid={`premium-mail-${item.id}`}
                    >
                      <Send className="w-3.5 h-3.5" />
                      Have us mail it
                    </button>
                  )}
                  <p className="text-[10px] text-white/55 mt-2 leading-relaxed" style={{ fontFamily: "Inter, sans-serif" }}>
                    We mail your letters to <strong>Equifax, Experian, & TransUnion</strong>.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AnalysisCard({ document, analysis, onRunAnalysis, running }) {
  const { user } = useAuth();
  const hasAddress = !!(user?.name && user?.address_line1 && user?.city && user?.state && user?.postal_code);
  const r = analysis?.result;
  const isRunning = analysis?.status === "running" || running;

  // In-progress state — shown while the background job is working.
  if (isRunning && !r) {
    return (
      <div className="mt-3 p-4 rounded-lg border border-[var(--if-gold)]/30 bg-[var(--if-gold)]/[0.04] flex items-start gap-3" data-testid={`analysis-running-${document.id}`}>
        <Sparkles className="w-5 h-5 text-[var(--if-gold)] shrink-0 mt-0.5 animate-pulse" />
        <div>
          <div className="font-mono-label text-[var(--if-gold)] mb-1">Sergeant Credit · Analyzing</div>
          <p className="text-sm text-white/70" style={{ fontFamily: "Inter, sans-serif" }}>
            Reading the report and drafting dispute angles. This usually takes 30–90 seconds for large files — you can leave this page and come back, the result will be saved.
          </p>
        </div>
      </div>
    );
  }

  // Error states
  if (r?.error === "not_a_credit_report") {
    return (
      <div className="mt-3 p-4 rounded-lg border border-amber-500/30 bg-amber-500/5 flex items-start gap-3" data-testid={`analysis-not-credit-${document.id}`}>
        <FileWarning className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
        <div>
          <div className="font-mono-label text-amber-300 mb-1">Not a credit report</div>
          <p className="text-sm text-white/70" style={{ fontFamily: "Inter, sans-serif" }}>{r.message}</p>
        </div>
      </div>
    );
  }

  if (r?.error) {
    return (
      <div className="mt-3 p-4 rounded-lg border border-red-500/30 bg-red-500/5 flex items-start gap-3" data-testid={`analysis-error-${document.id}`}>
        <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="font-mono-label text-red-300 mb-1">Analysis failed</div>
          <p className="text-sm text-white/70" style={{ fontFamily: "Inter, sans-serif" }}>{r.message}</p>
          <button onClick={onRunAnalysis} disabled={running} className="btn-outline !py-1.5 !px-3 !text-[11px] mt-2" data-testid={`retry-analysis-${document.id}`}>
            {running ? "Re-analyzing…" : "Try again"}
          </button>
        </div>
      </div>
    );
  }

  if (!r) return null;

  const items = r.negative_items || [];
  const meta = r.report_meta || {};
  const summary = r.summary || {};
  const impactColor =
    summary.estimated_score_impact === "high" ? "text-red-300" :
    summary.estimated_score_impact === "medium" ? "text-amber-300" : "text-green-300";

  return (
    <div className="mt-3 p-4 rounded-lg border border-[var(--if-gold)]/30 bg-[var(--if-gold)]/[0.04]" data-testid={`analysis-card-${document.id}`}>
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-[var(--if-gold)]" />
        <span className="font-mono-label text-[var(--if-gold)]">Sergeant Credit · Analysis</span>
        <span className="ml-auto text-[10px] text-white/40 uppercase tracking-wider" style={{ fontFamily: "Inter, sans-serif" }}>
          GPT-5.2
        </span>
      </div>

      {summary.headline && (
        <p className="text-sm text-white/85 mb-3 italic" style={{ fontFamily: "Inter, sans-serif" }}>
          "{summary.headline}"
        </p>
      )}

      <div className="grid grid-cols-3 gap-2 mb-4 text-center">
        <div className="p-2 rounded-lg bg-black/20 border border-white/5">
          <div className="font-mono-label text-white/50">Negative items</div>
          <div className="font-display text-2xl text-white">{summary.total_negative_items ?? items.length}</div>
        </div>
        <div className="p-2 rounded-lg bg-black/20 border border-white/5">
          <div className="font-mono-label text-white/50">Score impact</div>
          <div className={`font-display text-lg uppercase ${impactColor}`}>{summary.estimated_score_impact || "—"}</div>
        </div>
        <div className="p-2 rounded-lg bg-black/20 border border-white/5">
          <div className="font-mono-label text-white/50">Bureaus</div>
          <div className="font-display text-sm text-white truncate">{(meta.bureaus_present || []).join(" · ") || "—"}</div>
        </div>
      </div>

      {(meta.credit_scores || []).length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {meta.credit_scores.map((s) => (
            <div key={`${s.bureau}-${s.score}`} className="px-3 py-1.5 rounded-full bg-black/30 border border-white/10 text-xs" style={{ fontFamily: "Inter, sans-serif" }}>
              <span className="text-white/55">{s.bureau}: </span>
              <span className="text-[var(--if-gold)] font-semibold">{s.score}</span>
            </div>
          ))}
        </div>
      )}

      {/* Expanded consultation sections — score factors, util, payment history, plan, payoff, funding */}
      <AnalysisExtras r={r} />

      {items.length > 0 && (
        <div className="space-y-2 mb-4" data-testid={`negative-items-list-${document.id}`}>
          {items.map((it) => <NegativeItem key={it.id} item={it} document={document} hasAddress={hasAddress} />)}
        </div>
      )}

      {(r.next_steps || []).length > 0 && (
        <div className="p-3 rounded-lg bg-black/20 border border-white/5 mb-3">
          <div className="font-mono-label text-[var(--if-gold)] mb-2">Next steps</div>
          <ul className="space-y-1.5">
            {r.next_steps.map((s, i) => (
              <li key={`step-${i}`} className="text-sm text-white/75 flex gap-2" style={{ fontFamily: "Inter, sans-serif" }}>
                <span className="text-[var(--if-gold)]">→</span>
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {r.disclaimer && (
        <p className="text-[10px] text-white/40 flex items-start gap-1.5 leading-relaxed" style={{ fontFamily: "Inter, sans-serif" }}>
          <ShieldAlert className="w-3 h-3 mt-0.5 shrink-0" />
          {r.disclaimer}
        </p>
      )}

      <div className="flex justify-end gap-2 mt-3 pt-3 border-t border-white/10">
        <button
          onClick={() => window.open(`/report/${document.id}?print=1`, "_blank", "noopener,noreferrer")}
          className="btn-outline !py-1.5 !px-3 !text-[11px]"
          data-testid={`download-report-${document.id}`}
        >
          <Download className="w-3 h-3" />
          Download / View full report
        </button>
        <button
          onClick={onRunAnalysis}
          disabled={running}
          className="btn-outline !py-1.5 !px-3 !text-[11px]"
          data-testid={`re-analyze-${document.id}`}
        >
          <Sparkles className="w-3 h-3" />
          {running ? "Re-analyzing…" : "Re-run analysis"}
        </button>
      </div>
    </div>
  );
}
